/**
 * LES GESTES SUR UN CRENEAU, ce qui se calcule sans ecran : la copie d'une
 * fiche, la cascade de ceux qui se recouvrent, la place du menu.
 *
 * POURQUOI UN MODULE A PART. Comme `decaler-horaire.ts`, il n'importe ni React
 * ni le SDK : la copie et la cascade se verifient d'un bout a l'autre sans
 * monter la semaine. Son seul import est un TYPE (`verbatimModuleSyntax`
 * l'efface), pour ne pas recopier la forme d'une creation de timesheet.
 */

import type { TimesheetCreateInput } from '@taki/data-model';

/** Le decalage d'une copie, en minutes : elle commence un quart d'heure plus tard. */
export const DECALAGE_DE_COPIE = 15;

const MINUTE_MS = 60_000;

const texteNonVide = (valeur: unknown): valeur is string =>
  typeof valeur === 'string' && valeur !== '';

/**
 * LA COPIE D'UN CRENEAU : tout ce que la fiche dit du travail (tache, projet,
 * renseignements, liens, duree), decale de `decalageMinutes`. `null` quand la
 * fiche n'a pas de debut lisible : sans debut, il n'y a rien a decaler.
 *
 * CE QUI NE SE COPIE PAS : l'identifiant et les champs standard (le SDK les
 * pose a la creation), et `invoiceId`. Une copie n'est pas deja facturee : lui
 * rendre la facture de l'original la ferait compter deux fois et la figerait.
 * Le sac `customFields` est copie en entier (superficiellement, ses valeurs
 * sont des scalaires), sauf `demoKey` : c'est la marque d'une ligne du jeu de
 * demonstration (`demo-dataset.ts`), et une copie faite a la main n'en est pas une.
 */
export function copieDeCreneau(
  fiche: Record<string, unknown>,
  orgId: string,
  decalageMinutes: number,
): TimesheetCreateInput | null {
  const debut = fiche['startAt'];
  if (!(debut instanceof Date) || Number.isNaN(debut.getTime())) return null;
  const type = fiche['type'];
  const workerId = fiche['workerId'];
  const duree = fiche['durationMinutes'];
  const facturable = fiche['billable'];
  const sac = fiche['customFields'];
  const entree: TimesheetCreateInput = {
    orgId,
    type: texteNonVide(type) ? type : 'journal',
    workerId: texteNonVide(workerId) ? workerId : '',
    startAt: new Date(debut.getTime() + decalageMinutes * MINUTE_MS),
  };
  if (texteNonVide(fiche['projectId'])) entree.projectId = fiche['projectId'];
  if (texteNonVide(fiche['taskId'])) entree.taskId = fiche['taskId'];
  if (texteNonVide(fiche['workOrderId'])) entree.workOrderId = fiche['workOrderId'];
  if (texteNonVide(fiche['contactId'])) entree.contactId = fiche['contactId'];
  if (texteNonVide(fiche['shiftId'])) entree.shiftId = fiche['shiftId'];
  if (typeof duree === 'number' && Number.isFinite(duree)) entree.durationMinutes = duree;
  if (typeof facturable === 'boolean') entree.billable = facturable;
  if (typeof sac === 'object' && sac !== null && !Array.isArray(sac)) {
    const { demoKey: _marqueDeDemo, ...renseignements } = sac as NonNullable<TimesheetCreateInput['customFields']>;
    entree.customFields = renseignements;
  }
  return entree;
}

/** Ce que la cascade lit d'un creneau : son jour, son debut et sa duree (meme unite). */
export interface CreneauACascader {
  id: string;
  day: number;
  start: number;
  duration: number;
}

/**
 * LA PROFONDEUR DE CHAQUE CRENEAU DANS LA CASCADE : le nombre de creneaux du
 * MEME jour qui le chevauchent ET le precedent (debut plus tot, ou meme debut
 * et rang plus petit dans l'ordre debut puis id, stable d'un rendu a l'autre).
 * Le premier d'un paquet vaut 0 ; une copie posee 15 minutes apres son
 * original vaut 1, et la grille la decale d'un cran pour qu'on la voie.
 * Chevauchement : les intervalles `[start, start + duration)` se coupent ;
 * deux creneaux bout a bout ne se chevauchent pas.
 */
export function profondeursDeCascade(
  creneaux: readonly CreneauACascader[],
): Map<string, number> {
  const ordre = [...creneaux].sort(
    (a, b) => a.start - b.start || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
  const profondeurs = new Map<string, number>();
  ordre.forEach((creneau, rang) => {
    let profondeur = 0;
    for (let precedent = 0; precedent < rang; precedent += 1) {
      const autre = ordre[precedent];
      if (
        autre !== undefined &&
        autre.day === creneau.day &&
        autre.start < creneau.start + creneau.duration &&
        creneau.start < autre.start + autre.duration
      ) {
        profondeur += 1;
      }
    }
    profondeurs.set(creneau.id, profondeur);
  });
  return profondeurs;
}

/**
 * LA PLACE DU MENU : au point du clic, et bascule a gauche ou au-dessus de ce
 * point quand il deborderait de l'ecran. Jamais sous `marge` du bord (un menu
 * plus grand que l'ecran reste colle a la marge haute et gauche).
 */
export function positionDuMenu(
  x: number,
  y: number,
  menu: { largeur: number; hauteur: number },
  ecran: { largeur: number; hauteur: number },
  marge = 8,
): { left: number; top: number } {
  const place = (point: number, taille: number, limite: number): number => {
    const decale = point + taille + marge > limite ? point - taille : point;
    return Math.max(marge, Math.min(decale, limite - taille - marge));
  };
  return {
    left: place(x, menu.largeur, ecran.largeur),
    top: place(y, menu.hauteur, ecran.hauteur),
  };
}
